import {
  FormSidebar,
  FormSidebarActions,
  FormSidebarCallouts,
} from "../layouts/FormSidebar";
import ButtonSave from "../form/ButtonSave";
import ButtonPreview from "../form/ButtonPreview";
import Modal, { ModalProvider, ModalTrigger } from "../Modal/Modal";
import HtmlResult, { getHtmlResult } from "./HtmlResult";
import { putKlientkontoErklaering } from "../../api/klientkontoerklaering";
import { format } from "date-fns";
import { replacer } from "../../util/json";
import ButtonPrint from "../form/ButtonPrint";
import { put } from "../../api/util";
import { Button } from "../Button";
import {
  useLocation,
  useNavigate,
  useRevalidator,
  useRouteLoaderData,
} from "react-router";
import { FormikProps } from "formik";
import {
  IKkeJson,
  KlientkontoerklaeringFormValues,
  KlientkontoerklaeringMetadata,
  Formulartype,
} from "../../interfaces/klientkontoerklaering";
import {
  KKE_FORM_VERSION,
  RESET_REVISOR_CONFIG,
  SAFE_FIELDS,
} from "../../constants/config";
import {
  getNonMatchingStrings,
  replaceNonMatchingWithDefaults,
  shortenToTwoDigits,
  specialRule,
} from "../../util/sanitization";
import { KlientkontoErklaering, Signeringstype } from "../../interfaces/api";
import UnderskrivModal from "../Modal/UnderskrivModal";
import { KKE_PREFILL } from "../../constants/klientkontoerklaering/prefill";
import { KKE_FORM_INITIAL_VALUES } from "../../constants/klientkontoerklaering/initialValues";
import IconLocked from "../icons/IconLocked";
import IconUnlock from "../icons/IconUnlock";
import IconCheckCircleChecked from "../icons/IconCheckCircleChecked";
import { ROUTES } from "../../constants/routes";
import { HomeLoaderResponse } from "../../interfaces/loaders";
import { checkFinal } from "../../util/klientkontoerklaering";

const KKEFormSideBar = ({
  metadata,
  advokatens_complete,
  revisor_complete,
  formik,
  kkeId,
  formularType,
  kke,
}: {
  metadata: KlientkontoerklaeringMetadata;
  advokatens_complete: boolean;
  revisor_complete: boolean;
  formik: FormikProps<KlientkontoerklaeringFormValues>;
  kkeId: string;
  formularType: Formulartype;
  kke: KlientkontoErklaering;
}) => {
  const { values, setValues, dirty } = formik;
  const { senestGemt, person, firma } = metadata;
  const {
    signeret,
    antalAdvokater,
    antalAdvokaterSigneret,
    revisorSigneret,
    interessentSigneret,
  } = kke;

  const data = useRouteLoaderData(ROUTES.homeIndex.id) as
    HomeLoaderResponse | undefined;

  const userInfo = data?.userInfo;

  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();
  const interessetErklaringMode =
    antalAdvokater === 1
      ? 0
      : !interessentSigneret
        ? 1
        : !revisorSigneret
          ? 2
          : 3;
  const standardErklaeringMode = interessetErklaringMode <= 1;
  const advokatEllerSelskab =
    person !== undefined
      ? "Advokatens"
      : firma !== undefined
        ? "Advokatselskabets"
        : "";
  const handleStateReset = (values: KlientkontoerklaeringFormValues) => {
    let newValues = { ...values };
    Object.keys(RESET_REVISOR_CONFIG).forEach((e, i) => {
      i = i + 1;

      const searchString = values[`revisors_handling_${i}_question`];
      if (!searchString) return;
      const searchField = shortenToTwoDigits(searchString, i);
      const exceptions = SAFE_FIELDS[searchField];
      const nonMatchingFields = getNonMatchingStrings(
        searchField,
        RESET_REVISOR_CONFIG[e],
        exceptions ?? [],
      );
      const specialRules = specialRule(searchString);
      let toBeReset = nonMatchingFields;
      if (specialRules) {
        toBeReset = nonMatchingFields.concat(specialRules);
      }

      newValues = replaceNonMatchingWithDefaults(newValues, toBeReset);
    });
    return newValues;
  };
  const demoMode = import.meta.env.VITE_IS_DEMO?.toLocaleLowerCase() === "true";

  return (
    <FormSidebar>
      <FormSidebarCallouts>
        {senestGemt && (
          <p className="text-muted fs-6 mb-1">
            Senest gemt d.{" "}
            {format(new Date(senestGemt), "d.M.yyyy' kl. 'HH:mm")}
          </p>
        )}
        {!senestGemt && <p className="text-muted fs-6 mb-1">Endnu ikke gemt</p>}
        {!standardErklaeringMode && interessetErklaringMode === 2 && (
          <>
            <div className="callout callout-success fs-6">
              <div className="d-flex gap-2">
                <IconLocked className="mt-1" /> {advokatEllerSelskab} erklæring
                er godkendt af {antalAdvokaterSigneret} af {antalAdvokater}{" "}
                interessenter og er derfor låst.
              </div>
              <button
                onClick={async () => {
                  await put({
                    query: `KlientkontoErklaering/FjernAlleSigneringer?ErklaeringId=${kkeId}`,
                  });
                  navigate(location.pathname, {
                    state: { refresh: new Date() },
                  });
                }}
                className="btn btn-sm btn-outline-success-subtle d-flex w-100 gap-1 py-2 align-items-center mt-3 fw-medium"
              >
                <IconUnlock /> Lås op og fjern {antalAdvokaterSigneret}{" "}
                {antalAdvokaterSigneret > 1 ? "godkendelser" : "godkendelse"}
              </button>
            </div>
          </>
        )}
        {!standardErklaeringMode &&
          interessetErklaringMode === 2 &&
          !signeret && (
            <>
              <div className="callout fs-6">
                {advokatEllerSelskab} erklæring kan godkendes
              </div>
            </>
          )}
        {!standardErklaeringMode && interessetErklaringMode === 2 && (
          <>
            <div className="callout fs-6">
              Revisors erklæring{" "}
              {revisor_complete ? "kan godkendes" : "skal udfyldes"}
            </div>
          </>
        )}
        {!standardErklaeringMode && interessetErklaringMode === 3 && (
          <>
            <div className="callout callout-success fs-6">
              <div className="d-flex gap-2">
                <IconLocked className="mt-1" />
                Erklæringen er indsendt af {antalAdvokaterSigneret} af{" "}
                {antalAdvokater} interessenter og er derfor låst.
              </div>
            </div>
            <div className="callout fs-6">
              {advokatEllerSelskab} erklæring skal godkendes
            </div>
          </>
        )}
        {standardErklaeringMode && !advokatens_complete && (
          <div className="callout fs-6">
            {advokatEllerSelskab} erklæring skal udfyldes
          </div>
        )}
        {standardErklaeringMode && advokatens_complete && !signeret && (
          <>
            <div className="callout fs-6">
              {advokatEllerSelskab} erklæring {revisorSigneret ? "skal" : "kan"}{" "}
              godkendes
            </div>
          </>
        )}
        {standardErklaeringMode && signeret && (
          <div className="callout callout-success fs-6">
            <div className="d-flex gap-2">
              <IconLocked className="mt-1" /> {advokatEllerSelskab} erklæring er
              godkendt og er derfor låst.
            </div>
            <button
              onClick={async () => {
                await put({
                  query: `KlientkontoErklaering/FjernAlleSigneringer?ErklaeringId=${kkeId}`,
                });
                navigate(location.pathname, {
                  state: { refresh: new Date() },
                });
              }}
              className="btn btn-sm btn-outline-success-subtle d-flex w-100 gap-1 py-2 align-items-center mt-3 fw-medium"
            >
              <IconUnlock /> Lås op og fjern godkendelse
            </button>
          </div>
        )}
        {standardErklaeringMode && advokatens_complete && !revisor_complete && (
          <div className="callout fs-6">Revisors erklæring skal udfyldes</div>
        )}
        {standardErklaeringMode &&
          advokatens_complete &&
          revisor_complete &&
          !revisorSigneret && (
            <div className="callout fs-6">
              Revisors erklæring {signeret ? "skal" : "kan"} godkendes
            </div>
          )}
        {standardErklaeringMode && advokatens_complete && revisorSigneret && (
          <div className="callout callout-success fs-6">
            <div className="d-flex gap-2">
              <IconLocked className="mt-1" /> Revisors erklæring er godkendt og
              er derfor låst.
            </div>
            <Button
              onClick={async () => {
                await put({
                  query: `KlientkontoErklaering/FjernAlleSigneringer?ErklaeringId=${kkeId}`,
                });
                navigate(location.pathname, {
                  state: { refresh: new Date() },
                });
              }}
              className="btn btn-sm btn-outline-success-subtle d-flex w-100 gap-1 py-2 align-items-center mt-3 fw-medium"
            >
              <IconUnlock /> Lås op og fjern godkendelse
            </Button>
          </div>
        )}
      </FormSidebarCallouts>
      <FormSidebarActions>
        <div className="d-flex gap-3">
          <ButtonSave
            disabled={!dirty}
            onClick={async function () {
              const newValues = handleStateReset(values);
              setValues(newValues);
              const jsonData = {
                ...JSON.parse(kke.jsonData),
                form: newValues,
              };

              await putKlientkontoErklaering({
                id: kkeId,
                jsonData: JSON.stringify(jsonData, replacer),
                formular: getHtmlResult(newValues, metadata),
                formularType,
              });
              if (revalidator.state === "idle") {
                revalidator.revalidate();
              }
            }}
          />
          <ModalProvider>
            <ModalTrigger asChild>
              <ButtonPreview />
            </ModalTrigger>
            <Modal
              title={
                <div className="d-flex w-100 align-items-center justify-content-between">
                  <span>Forhåndsvisning</span>
                  <ButtonPrint />
                </div>
              }
            >
              <HtmlResult values={values} metadata={metadata} />
            </Modal>
          </ModalProvider>
        </div>
        {signeret ? (
          <Button
            size="lg"
            color="success"
            subtle
            className="d-flex align-items-center justify-content-between"
            disabled
          >
            <span className="d-flex flex-column align-items-start">
              Godkend med MitID
              <small className="fw-medium tiny opacity-75">
                {advokatEllerSelskab} erklæring
              </small>
            </span>
            <IconCheckCircleChecked />
          </Button>
        ) : (
          <ModalProvider>
            <ModalTrigger asChild>
              <Button
                color="primary"
                size="lg"
                className="d-flex flex-column"
                disabled={
                  !advokatens_complete ||
                  dirty ||
                  userInfo?.identityType === "Accountant"
                }
              >
                Godkend med MitID
                <small className="fw-medium tiny opacity-75">
                  {advokatEllerSelskab} erklæring
                </small>
              </Button>
            </ModalTrigger>
            <UnderskrivModal
              isFinal={checkFinal(kke)}
              onSubmitHandler={async ({ email }) => {
                const signeringstype: Signeringstype = "Klientkontoerklaering";
                const kke_json: IKkeJson = JSON.parse(kke.jsonData);
                try {
                  const mail = email ? email : null;
                  const jsonData = {
                    form: formik.values,
                    version: KKE_FORM_VERSION,
                    kvittering_emailadresse: {
                      advokat:
                        signeringstype === "Klientkontoerklaering"
                          ? mail
                          : (kke_json.kvittering_emailadresse?.advokat ?? null),
                      revisor:
                        kke_json.kvittering_emailadresse?.revisor ?? null,
                    },
                  };
                  await putKlientkontoErklaering({
                    id: kkeId,
                    jsonData: JSON.stringify(jsonData, replacer),
                    formular: getHtmlResult(formik.values, metadata),
                    formularType,
                  });
                  if (revalidator.state === "idle") {
                    revalidator.revalidate();
                  }
                  window.location.href = `${
                    import.meta.env.VITE_BACKEND_ENDPOINT
                  }/Signering/Signer?erklaeringId=${kkeId}&signeringstype=${signeringstype}`;
                } catch (error) {
                  console.error(error);
                }
              }}
            />
          </ModalProvider>
        )}
        {revisorSigneret ? (
          <Button
            size="lg"
            color="success"
            subtle
            className="d-flex align-items-center justify-content-between"
            disabled
          >
            <span className="d-flex flex-column align-items-start">
              Godkend med MitID
              <small className="fw-medium tiny opacity-75">
                Revisors erklæring
              </small>
            </span>
            <IconCheckCircleChecked />
          </Button>
        ) : (
          <ModalProvider>
            <ModalTrigger asChild>
              <Button
                size="lg"
                color="primary"
                className="d-flex flex-column"
                type="button"
                disabled={
                  !revisor_complete ||
                  dirty ||
                  userInfo?.identityType !== "Accountant"
                }
              >
                Godkend med MitID
                <small className="fw-medium tiny opacity-75">
                  Revisors erklæring
                </small>
              </Button>
            </ModalTrigger>
            <UnderskrivModal
              isFinal={checkFinal(kke)}
              onSubmitHandler={async ({ email }) => {
                const signeringstype: Signeringstype =
                  "KlientkontoerklaeringRevisor";
                const kke_json: IKkeJson = JSON.parse(kke.jsonData);
                try {
                  const mail = email ? email : null;
                  const jsonData = {
                    form: formik.values,
                    version: KKE_FORM_VERSION,
                    kvittering_emailadresse: {
                      advokat:
                        kke_json.kvittering_emailadresse?.advokat ?? null,
                      revisor:
                        signeringstype === "KlientkontoerklaeringRevisor"
                          ? mail
                          : (kke_json.kvittering_emailadresse?.revisor ?? null),
                    },
                  };
                  await putKlientkontoErklaering({
                    id: kkeId,
                    jsonData: JSON.stringify(jsonData, replacer),
                    formular: getHtmlResult(formik.values, metadata),
                    formularType,
                  });
                  if (revalidator.state === "idle") {
                    revalidator.revalidate();
                  }
                  window.location.href = `${
                    import.meta.env.VITE_BACKEND_ENDPOINT
                  }/Signering/Signer?erklaeringId=${kkeId}&signeringstype=${signeringstype}&forceLogin=false`;
                } catch (error) {
                  console.error(error);
                }
              }}
            />
          </ModalProvider>
        )}
      </FormSidebarActions>
      {demoMode && (
        <>
          <button
            type="button"
            className="mt-4 btn btn-secondary border"
            onClick={async () => {
              await putKlientkontoErklaering({
                id: kkeId,
                jsonData: JSON.stringify(KKE_PREFILL, replacer),
                formular: getHtmlResult(KKE_FORM_INITIAL_VALUES, metadata),
                formularType,
              });
              if (revalidator.state === "idle") {
                revalidator.revalidate();
              }
            }}
          >
            Prefill form
          </button>
          <button
            type="button"
            className="btn btn-secondary border mt-4"
            onClick={async () => {
              await putKlientkontoErklaering({
                id: kkeId,
                jsonData: JSON.stringify(KKE_FORM_INITIAL_VALUES, replacer),
                formular: getHtmlResult(KKE_FORM_INITIAL_VALUES, metadata),
                formularType,
              });
              if (revalidator.state === "idle") {
                revalidator.revalidate();
              }
            }}
          >
            Clear form
          </button>
        </>
      )}
    </FormSidebar>
  );
};

export default KKEFormSideBar;
